import './shortcodes.css'

import { computed, createApp, h } from 'vue'
import { createPinia } from 'pinia'

import Flashcard from '../flashcard/Flashcard.vue'
import { buildFlashcardProps } from '../editor/helpers/flashcardProps'
import type { EditorCardProps } from '../editor/store'

import { FLASHCARD_DISABLE_AUTOPLAY } from '../flashcard/context'
import { coerceDeckTemplateSettings, resolveDeckTemplateSettings } from '../deck/templateSettings'

import { normalizeCardJsonV1, type CardJsonV1 } from '../data/cardsApi'
import { coerceEditorDocument, getAdminContext, wpFetch } from '../data/wpApi'
import type { FlashcardElementInput } from '../flashcard/elements/registry'
import { installUserSettings } from '../userSettings/installUserSettings'
import { useUserSettingsStore } from '../stores/useUserSettingsStore'

import { mountDeckPlayer } from '../deckplayer/integration/mountDeckPlayer'
import {
  mountDeckPlayerCountWidget,
  mountDeckPlayerProgressWidget,
  mountDeckPlayerButtonWidget,
} from '../deckplayer/entrypoints'

type MountTarget = Element | string

type MountOptions = {
  postId: number
  restUrl?: string
  nonce?: string
  interactive?: boolean
  forceFace?: 'front' | 'back'
  allowOverflow?: boolean
  disableAutoplay?: boolean
}

type MountAllOptions = Omit<MountOptions, 'postId'> & {
  selector?: string
  root?: ParentNode
  attribute?: string
}

const getBootstrap = () => {
  const ctx = getAdminContext()
  if (!ctx) return null
  return { restUrl: ctx.restUrl, nonce: ctx.nonce }
}

const resolveTarget = (target: MountTarget): Element | null => {
  if (typeof target === 'string') {
    return document.querySelector(target)
  }
  return target
}

const normalizeCardJson = (json: unknown): CardJsonV1<FlashcardElementInput> | null => {
  const payload = coerceEditorDocument(json)
  return normalizeCardJsonV1<FlashcardElementInput>(payload)
}

const parseJsonObjectAttribute = (
  el: Element,
  attribute: string,
): Record<string, unknown> | null => {
  const raw = el.getAttribute(attribute)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    return parsed as Record<string, unknown>
  } catch {
    return null
  }
}

const coerceFlag = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (v === 'true' || v === '1' || v === 'yes' || v === 'on') return true
    if (v === 'false' || v === '0' || v === 'no' || v === 'off') return false
  }
  return undefined
}

const omitFalseTemplateFlags = (input: Record<string, unknown> | null | undefined) => {
  if (!input) return null

  // Elementor/shortcode builders often persist unchecked toggles as explicit `false`.
  // For template-style props, treat `false` as "unset" so deck/card defaults can apply.
  const keys = [
    'darkMode',
    'dark_mode',
    'hoverFlip',
    'hover_flip',
    'showBackFirst',
    'show_back_first',
    'disableAutoplay',
    'disable_autoplay',
    'audioAutoplay',
    'audio_autoplay',
    'audioNotVisible',
    'audio_not_visible',
    'audioForceVisible',
    'audio_force_visible',
  ]

  const out: Record<string, unknown> = { ...input }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(out, key) && out[key] === false) {
      delete out[key]
    }
  }
  return out
}

type DeckPlayerButtonType = 'next' | 'back' | 'skip' | 'undo' | 'redo' | 'reset'

const coerceNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const v = value.trim()
  return v ? v : undefined
}

const getDeckIdFromElement = (el: Element): string | null => {
  const raw =
    el.getAttribute('data-pfc-deck-id') ||
    el.getAttribute('data-pfc-deckId') ||
    el.getAttribute('data-deck-id')

  if (!raw) return null
  const deckId = raw.trim()
  return deckId ? deckId : null
}

const getDeckPlayerButtonFromWidgetType = (widgetType: string): DeckPlayerButtonType | null => {
  if (!widgetType.startsWith('deckplayer-btn-')) return null
  const raw = widgetType.slice('deckplayer-btn-'.length).trim().toLowerCase()
  if (
    raw === 'next' ||
    raw === 'back' ||
    raw === 'skip' ||
    raw === 'undo' ||
    raw === 'redo' ||
    raw === 'reset'
  ) {
    return raw
  }
  return null
}

const mountDeckPlayerWidgets = async (options: { root?: ParentNode } = {}) => {
  const root = options.root || document
  const nodes = Array.from(root.querySelectorAll('[data-deckplayer-widget]'))

  const tasks = nodes.map(async (el) => {
    const widgetType = el.getAttribute('data-deckplayer-widget') || ''
    const deckId = getDeckIdFromElement(el)
    const attrProps = parseJsonObjectAttribute(el, 'data-pfc-props')

    if (widgetType === 'deckplayer-count') {
      if (!deckId) {
        console.warn('PerlingoFlashcards.mountDeckPlayerWidgets: missing data-pfc-deck-id', el)
        return
      }
      await mountDeckPlayerCountWidget(el, { props: { deckId, ...attrProps } })
      return
    }

    if (widgetType === 'deckplayer-progress') {
      if (!deckId) {
        console.warn('PerlingoFlashcards.mountDeckPlayerWidgets: missing data-pfc-deck-id', el)
        return
      }
      await mountDeckPlayerProgressWidget(el, { props: { deckId, ...attrProps } })
      return
    }

    const button = getDeckPlayerButtonFromWidgetType(widgetType)
    if (!button) return

    if (!deckId) {
      console.warn('PerlingoFlashcards.mountDeckPlayerWidgets: missing data-pfc-deck-id', el)
      return
    }

    const allowSkip =
      coerceFlag(attrProps?.allowSkip) ??
      coerceFlag(attrProps?.allow_skip) ??
      coerceFlag(el.getAttribute('data-pfc-allow-skip'))

    const label =
      coerceNonEmptyString(attrProps?.label) ??
      coerceNonEmptyString(el.getAttribute('data-pfc-label'))

    await mountDeckPlayerButtonWidget(el, {
      props: {
        deckId,
        button,
        allowSkip,
        label,
        ...attrProps,
      },
    })
  })

  await Promise.allSettled(tasks)
}

const mountFlashcard = async (target: MountTarget, options: MountOptions) => {
  const el = resolveTarget(target)
  if (!el) {
    console.warn('PerlingoFlashcards.mountFlashcard: target not found', target)
    return
  }

  const attrProps = parseJsonObjectAttribute(el, 'data-pfc-props')
  const disableAutoplay =
    options.disableAutoplay ??
    coerceFlag(attrProps?.disableAutoplay) ??
    coerceFlag(attrProps?.disable_autoplay) ??
    false

  const bootstrap = getBootstrap()
  const restUrl = options.restUrl || bootstrap?.restUrl
  const nonce = options.nonce || bootstrap?.nonce

  if (!restUrl) {
    console.warn(
      'PerlingoFlashcards.mountFlashcard: missing restUrl (options.restUrl or window.PFC_ADMIN.restUrl)',
    )
    return
  }

  const postId = Number(options.postId)
  if (!Number.isFinite(postId) || postId <= 0) {
    console.warn('PerlingoFlashcards.mountFlashcard: invalid postId', options.postId)
    return
  }

  const url = `${restUrl}/cards/${postId}/json`
  const response = await wpFetch(url, { method: 'GET' }, nonce)
  if (!response.ok) {
    console.warn('PerlingoFlashcards.mountFlashcard: failed to load card JSON', response.status)
    return
  }

  const json = (await response.json()) as unknown
  const normalized = normalizeCardJson(json)
  if (!normalized) {
    console.warn('PerlingoFlashcards.mountFlashcard: unexpected card JSON shape', json)
    return
  }

  // Unmount prior instance on the same element.
  const previousUnmount = (el as unknown as { __pfcUnmount?: (() => void) | undefined })
    .__pfcUnmount
  if (previousUnmount) {
    try {
      previousUnmount()
    } catch {
      // ignore
    }
  }

  const interactive = options.interactive !== false
  const forceFace = options.forceFace
  const allowOverflow = options.allowOverflow === true

  const flashcardProps = buildFlashcardProps(normalized.props as unknown as EditorCardProps)

  const app = createApp({
    render: () =>
      h(Flashcard, {
        class: 'pfc-shortcode-flashcard-inner',
        frontElements: normalized.frontElements,
        backElements: normalized.backElements,
        interactive,
        forceFace,
        allowOverflow,
        ...flashcardProps,
        ...resolvedTemplateProps.value,
      }),
  })

  const pinia = createPinia()
  app.use(pinia)
  installUserSettings(app, pinia)

  const userSettings = useUserSettingsStore(pinia)

  const resolvedTemplateProps = computed(() =>
    resolveDeckTemplateSettings({
      user: { forceDarkMode: userSettings.forceDarkMode },
      embed: coerceDeckTemplateSettings(omitFalseTemplateFlags(attrProps)),
      card: coerceDeckTemplateSettings(flashcardProps as unknown),
    }),
  )

  const resolvedDisableAutoplay = computed(() => disableAutoplay)

  app.provide(FLASHCARD_DISABLE_AUTOPLAY, resolvedDisableAutoplay)

  app.mount(el)
  ;(el as unknown as { __pfcUnmount?: (() => void) | undefined }).__pfcUnmount = () => app.unmount()
}

const mountAll = async (options: MountAllOptions = {}) => {
  const selector = options.selector || '.pfc-shortcode-flashcard[data-pfc-flashcard]'
  const attribute = options.attribute || 'data-pfc-flashcard'
  const root = options.root || document

  const nodes = Array.from(root.querySelectorAll(selector))
  const tasks = nodes.map(async (el) => {
    const rawId = el.getAttribute(attribute)
    const postId = rawId ? Number(rawId) : NaN

    if (!Number.isFinite(postId) || postId <= 0) {
      console.warn('PerlingoFlashcards.mountAll: invalid data-pfc-flashcard value', rawId)
      return
    }

    await mountFlashcard(el, {
      postId,
      restUrl: options.restUrl,
      nonce: options.nonce,
      interactive: options.interactive,
      forceFace: options.forceFace,
      allowOverflow: options.allowOverflow,
    })
  })

  await Promise.allSettled(tasks)
}

const installGlobal = () => {
  const win = window as unknown as {
    PerlingoFlashcards?: Record<string, unknown>
  }

  if (!win.PerlingoFlashcards) {
    win.PerlingoFlashcards = {}
  }

  ;(win.PerlingoFlashcards as Record<string, unknown>).mountFlashcard = mountFlashcard
  ;(win.PerlingoFlashcards as Record<string, unknown>).mountAll = mountAll

  // Deckplayer shortcode integration (WP helper calls this when present)
  ;(win.PerlingoFlashcards as Record<string, unknown>).mountDeckPlayer = mountDeckPlayer

  // Advanced deckplayer widgets (Elementor/shortcodes)
  ;(win.PerlingoFlashcards as Record<string, unknown>).mountDeckPlayerCountWidget =
    mountDeckPlayerCountWidget
  ;(win.PerlingoFlashcards as Record<string, unknown>).mountDeckPlayerProgressWidget =
    mountDeckPlayerProgressWidget
  ;(win.PerlingoFlashcards as Record<string, unknown>).mountDeckPlayerButtonWidget =
    mountDeckPlayerButtonWidget
  ;(win.PerlingoFlashcards as Record<string, unknown>).mountDeckPlayerWidgets =
    mountDeckPlayerWidgets

  // Standalone flowchart editor overlay — lazy-loaded so FlowManagerView is not bundled
  // into the shortcodes bundle. Called by perlingo-courses admin when pfc-shortcodes is loaded.
  ;(win.PerlingoFlashcards as Record<string, unknown>).mountFlowEditor = (
    options: Record<string, unknown>,
  ): { unmount: () => void } => {
    const overlay = document.createElement('div')
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:999999;background:#fff;overflow:hidden;display:flex;align-items:center;justify-content:center;font-size:1.1rem;color:#64748b;'
    overlay.textContent = 'Lade Editor…'
    document.body.appendChild(overlay)

    let inner: { unmount: () => void } | null = null

    void import('../deckplayer/integration/mountFlowEditor').then(({ mountFlowEditor }) => {
      overlay.remove()
      inner = mountFlowEditor(options as Parameters<typeof mountFlowEditor>[0])
    }).catch(() => overlay.remove())

    return {
      unmount: () => { if (inner) inner.unmount(); else overlay.remove() },
    }
  }
}

installGlobal()
